import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  FileDownload,
  PictureAsPdf,
  CalendarToday,
  ViewModule,
  Star,
  TrendingUp,
  Timeline,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { colors, transitions, radius } from '../theme/theme';

// Using the service usage hook - we need to create this
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';

const CHART_COLORS = [
  colors.primary.main,
  colors.success.main,
  colors.warning.main,
  colors.error.main,
  colors.info.main,
  colors.secondary.main,
];

export const ServiceUsageReportPage = () => {
  const [period, setPeriod] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Calculate dates based on selected period
  const getDateRange = () => {
    const now = new Date();
    let start = '';
    let end = '';

    switch (period) {
      case 'today':
        start = end = now.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        start = lastWeekStart.toISOString().split('T')[0];
        end = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        start = lastMonthStart.toISOString().split('T')[0];
        end = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate;
        end = endDate;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const dateRange = getDateRange();
  const shouldFetch = period !== 'custom' || (!!startDate && !!endDate);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['reports', 'service-usage', dateRange.start, dateRange.end],
    queryFn: async () => {
      const response = await apiClient.get('/admin/county/reports/service-usage', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
        },
      });
      return response.data;
    },
    enabled: shouldFetch,
  });

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Service Usage Report"
          subtitle="Analyze service performance and usage patterns"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' },
            { label: 'Service Usage Report' },
          ]}
          action={
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                disabled={!shouldFetch}
                size="small"
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                disabled={!shouldFetch}
                size="small"
              >
                PDF
              </Button>
            </Box>
          }
        />

        {/* Period Selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ color: colors.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Time Period
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={period}
                exclusive
                onChange={(e, newPeriod) => newPeriod && setPeriod(newPeriod)}
                size="small"
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                  },
                  '& .Mui-selected': {
                    bgcolor: `${colors.primary.main} !important`,
                    color: 'white !important',
                  },
                }}
              >
                <ToggleButton value="today">Today</ToggleButton>
                <ToggleButton value="yesterday">Yesterday</ToggleButton>
                <ToggleButton value="this_week">This Week</ToggleButton>
                <ToggleButton value="last_week">Last Week</ToggleButton>
                <ToggleButton value="this_month">This Month</ToggleButton>
                <ToggleButton value="last_month">Last Month</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Collapse in={period === 'custom'}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load service usage report. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Summary Stats */}
            <Grid container spacing={3} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Services"
                  value={report.totalServices?.toLocaleString() || 0}
                  icon={<ViewModule />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Active Services"
                  value={report.activeServices?.toLocaleString() || 0}
                  icon={<TrendingUp />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Average Usage"
                  value={Math.round(report.averageUsage || 0)}
                  icon={<Timeline />}
                  color={colors.info.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Revenue"
                  value={`KES ${report.totalRevenue?.toLocaleString() || 0}`}
                  icon={<AccountBalanceWallet />}
                  color={colors.secondary.main}
                />
              </Grid>
            </Grid>

            {/* Most Used Service Highlight */}
            {report.mostUsedService && (
              <Card sx={{ mb: 3, bgcolor: colors.primary.light, borderLeft: `4px solid ${colors.primary.main}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Star sx={{ fontSize: 40, color: colors.primary.main }} />
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Most Used Service
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary.main }}>
                        {report.mostUsedService.service_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {report.mostUsedService.total_transactions?.toLocaleString()} transactions • KES{' '}
                        {report.mostUsedService.total_revenue?.toLocaleString()} revenue
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Charts Section */}
            <Grid container spacing={3} mb={3}>
              {/* Top Services by Usage */}
              {report.serviceStats && report.serviceStats.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Top Services by Transactions
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={report.serviceStats.slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                          <XAxis
                            dataKey="service_name"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="total_transactions" fill={colors.primary.main} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Revenue Distribution */}
              {report.serviceStats && report.serviceStats.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Revenue Distribution (Top 6)
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={report.serviceStats.slice(0, 6).map((item: any) => ({
                              name: item.service_name,
                              value: item.total_revenue,
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={(entry: any) => entry.name}
                          >
                            {report.serviceStats.slice(0, 6).map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => `KES ${value.toLocaleString()}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Service Stats DataGrid */}
            {report.serviceStats && report.serviceStats.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Detailed Service Analytics
                  </Typography>
                  <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                      rows={report.serviceStats.map((service: any, index: number) => ({
                        id: service.service_id || index,
                        ...service,
                      }))}
                      columns={[
                        {
                          field: 'service_name',
                          headerName: 'Service Name',
                          flex: 1,
                          minWidth: 200,
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={600}>
                              {params.value}
                            </Typography>
                          ),
                        },
                        {
                          field: 'service_code',
                          headerName: 'Code',
                          width: 120,
                          renderCell: (params) => (
                            <Chip
                              label={params.value || 'N/A'}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ),
                        },
                        {
                          field: 'total_transactions',
                          headerName: 'Total Transactions',
                          width: 160,
                          type: 'number',
                          renderCell: (params) => (
                            <Chip
                              label={params.value?.toLocaleString() || 0}
                              size="small"
                              sx={{ bgcolor: colors.primary.light, color: colors.primary.dark, fontWeight: 600 }}
                            />
                          ),
                        },
                        {
                          field: 'successful_transactions',
                          headerName: 'Successful',
                          width: 120,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" color={colors.success.main} fontWeight={600}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'failed_transactions',
                          headerName: 'Failed',
                          width: 100,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" color={colors.error.main} fontWeight={600}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'total_revenue',
                          headerName: 'Revenue (KES)',
                          width: 150,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={700} color={colors.secondary.main}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'last_used',
                          headerName: 'Last Used',
                          width: 160,
                          renderCell: (params) => (
                            <Typography variant="body2" color="text.secondary">
                              {params.value !== 'Never' ? new Date(params.value).toLocaleDateString() : 'Never'}
                            </Typography>
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        sorting: {
                          sortModel: [{ field: 'total_transactions', sort: 'desc' }],
                        },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                      disableRowSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No Data Message */}
            {(!report.serviceStats || report.serviceStats.length === 0) && (
              <Card>
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <ViewModule sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Service Usage Data
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      There is no service usage data for the selected period.
                      <br />
                      Try selecting a different date range.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <Typography color="text.secondary">
                  Select a date range to generate service usage report
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </AdminLayout>
  );
};
